import React, { FC } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'

import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DeviceLinkModel } from 'src/Libs/installations/models'

interface UseInstalledSystemsTableData {
  fetchProps: FetchProps
  columns: ApiTableColumn<DeviceLinkModel>[]
}

const emptySymbol = '-'

const InstallationIdColumn: FC<DeviceLinkModel> = ({ installationId }) => {
  if (!installationId) {
    return emptySymbol
  }

  return (
    <Link component={RouterLink} variant="body2" to={`/overview/installations/${installationId}`}>
      {installationId}
    </Link>
  )
}

const SystemColumn: FC<DeviceLinkModel> = ({ installationId, deviceId }) => {
  if (!deviceId || !installationId) {
    return emptySymbol
  }

  return (
    <Link component={RouterLink} variant="body2" to={`/overview/installations/${installationId}/${deviceId}/details`}>
      {deviceId}
    </Link>
  )
}

interface OwnerProps extends DeviceLinkModel {
  currentUserId: number
}

const OwnerColumn: FC<OwnerProps> = ({ ownerId, ownerFullName, currentUserId }) => {
  if (!ownerId || !ownerFullName) {
    return '-'
  }

  if (ownerId === `${currentUserId}`) {
    return ownerFullName
  }

  return (
    <Link component={RouterLink} variant="body2" to={`/overview/users/${ownerId}`}>
      {ownerFullName}
    </Link>
  )
}

/**
 * Table data for table of systems installed by the user
 * @param userId
 * @param currentUserId
 */
export const useInstalledSystemsTableData = (userId: string, currentUserId: number): UseInstalledSystemsTableData => {
  const { t } = useCustomTranslation()

  const fetchProps: FetchProps = {
    url: `installations/device-links`,
    filter: `installedBy=${userId}`,
    orderBy: '-installationId',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  }

  const columns: ApiTableColumn<DeviceLinkModel>[] = [
    {
      name: 'installationId',
      label: t('service-dashboard.users.installed-installations.table.installation-id'),
      value: InstallationIdColumn,
    },
    {
      name: 'deviceId',
      label: t('service-dashboard.users.installed-installations.table.serial-number'),
      value: SystemColumn,
    },
    {
      name: 'productName',
      label: t('service-dashboard.users.installed-installations.table.type'),
      value: ({ productName }) => productName ?? '-',
    },
    {
      name: 'ownerId',
      label: t('service-dashboard.users.installed-installations.table.owner'),
      value: props => <OwnerColumn {...props} currentUserId={currentUserId} />,
    },
  ]

  return { fetchProps, columns }
}
